import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  ElementOverrideMap,
  ElementStyle,
  SelectedElement,
  ELEMENT_OVERRIDES_STORAGE_KEY,
} from "@/lib/elementOverrideTypes";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface ElementOverrideContextValue {
  /** All element overrides */
  overrides: ElementOverrideMap;
  /** Get resolved style for a specific element + mode */
  getElementStyle: (designId: string, mode: "light" | "dark") => ElementStyle;
  /** Set a single property for an element */
  setElementProp: (designId: string, mode: "light" | "dark", prop: keyof ElementStyle, value: string) => void;
  /** Reset an element to defaults (remove overrides) */
  resetElement: (designId: string) => void;
  /** Save all overrides to cloud */
  saveToCloud: () => Promise<void>;
  isSaving: boolean;
  /** Selection mode */
  isSelectMode: boolean;
  setSelectMode: (on: boolean) => void;
  /** Currently selected element */
  selectedElement: SelectedElement | null;
  setSelectedElement: (el: SelectedElement | null) => void;
}

const ElementOverrideContext = createContext<ElementOverrideContextValue | null>(null);

export function useElementOverrides() {
  const ctx = useContext(ElementOverrideContext);
  if (!ctx) throw new Error("useElementOverrides must be inside ElementOverrideProvider");
  return ctx;
}

function loadFromStorage(): ElementOverrideMap {
  try {
    const raw = localStorage.getItem(ELEMENT_OVERRIDES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(data: ElementOverrideMap) {
  localStorage.setItem(ELEMENT_OVERRIDES_STORAGE_KEY, JSON.stringify(data));
}

export function ElementOverrideProvider({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin();
  const [overrides, setOverrides] = useState<ElementOverrideMap>(loadFromStorage);
  const overridesRef = useRef(overrides);
  const [isSaving, setIsSaving] = useState(false);
  const [isSelectMode, setSelectMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(null);
  const initialLoadDone = useRef(false);
  const readyToSave = useRef(false);
  const isFromRealtime = useRef(false);
  const isSavingRef = useRef(false);
  
  // Keep ref in sync so saveToCloud always has latest data
  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  // Clear selected element when URL changes (element reference becomes stale)
  useEffect(() => {
    const handlePopState = () => setSelectedElement(null);
    window.addEventListener("popstate", handlePopState);
    let lastPath = window.location.pathname;
    const interval = setInterval(() => {
      if (window.location.pathname !== lastPath) {
        lastPath = window.location.pathname;
        setSelectedElement(null);
      }
    }, 300);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      clearInterval(interval);
    };
  }, []);

  // Load from global_design_defaults on mount (no auth needed for read)
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      try {
        const { data } = await (supabase as any)
          .from("global_design_defaults")
          .select("config")
          .eq("page_scope", "element-overrides")
          .maybeSingle();

        if (data?.config && typeof data.config === "object") {
          const cloudOverrides = data.config as unknown as ElementOverrideMap;
          setOverrides(cloudOverrides);
          saveToStorage(cloudOverrides);
        }
      } finally {
        readyToSave.current = true;
      }
    })();
  }, []);

  // Realtime: listen for element override changes from other admins
  useEffect(() => {
    const channel = supabase
      .channel('global-element-overrides')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'global_design_defaults', filter: 'page_scope=eq.element-overrides' },
        (payload: any) => {
          if (isSavingRef.current) return;
          const row = payload.new;
          if (!row?.config) return;
          const newOverrides = row.config as unknown as ElementOverrideMap;
          isFromRealtime.current = true;
          setOverrides(newOverrides);
          saveToStorage(newOverrides);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const getElementStyle = useCallback((designId: string, mode: "light" | "dark"): ElementStyle => {
    return overrides[designId]?.[mode] || {};
  }, [overrides]);

  const setElementProp = useCallback((designId: string, mode: "light" | "dark", prop: keyof ElementStyle, value: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (!next[designId]) next[designId] = { light: {}, dark: {} };
      if (!next[designId][mode]) next[designId][mode] = {};
      next[designId][mode][prop] = value;
      saveToStorage(next);
      return next;
    });
  }, []);

  const resetElement = useCallback((designId: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[designId];
      saveToStorage(next);
      return next;
    });
  }, []);

  // DOM fallback: apply overrides to elements with data-design-id that
  // don't have React-level getElementStyle() wiring.
  // This runs after every override change and on route/DOM changes.
  useEffect(() => {
    const mode = document.documentElement.classList.contains("dark") ? "dark" : "light";
    
    const applyOverridesToDOM = () => {
      const allDesignEls = document.querySelectorAll<HTMLElement>('[data-design-id]');
      allDesignEls.forEach(el => {
        const designId = el.dataset.designId!;
        const style = overrides[designId]?.[mode];
        if (!style) return;
        
        if (style.backgroundColor) el.style.backgroundColor = style.backgroundColor;
        if (style.borderColor) {
          el.style.borderColor = style.borderColor;
          el.style.borderStyle = "solid";
        }
        if (style.borderWidth) el.style.borderWidth = `${style.borderWidth}px`;
        if (style.textColor) el.style.color = style.textColor;
        if (style.iconColor) {
          const svgs = el.querySelectorAll<SVGElement>('svg');
          svgs.forEach(svg => { (svg as any).style.color = style.iconColor; });
          const iconDivs = el.querySelectorAll<HTMLElement>('.text-primary, .text-accent');
          iconDivs.forEach(div => { div.style.color = style.iconColor!; });
        }
      });
    };

    // Apply immediately + delayed to catch late-rendered elements
    applyOverridesToDOM();
    const t1 = setTimeout(applyOverridesToDOM, 150);
    const t2 = setTimeout(applyOverridesToDOM, 500);
    
    // Watch for new elements being added to the DOM (route changes, lazy loads)
    const domObserver = new MutationObserver((mutations) => {
      let hasNewDesignEls = false;
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (node instanceof HTMLElement) {
            if (node.dataset?.designId || node.querySelector?.('[data-design-id]')) {
              hasNewDesignEls = true;
              break;
            }
          }
        }
        if (hasNewDesignEls) break;
      }
      if (hasNewDesignEls) {
        requestAnimationFrame(applyOverridesToDOM);
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
    
    // Re-apply when theme changes
    const themeObserver = new MutationObserver(() => {
      setTimeout(applyOverridesToDOM, 50);
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      domObserver.disconnect();
      themeObserver.disconnect();
    };
  }, [overrides]);

  const saveToCloudFn = useCallback(async () => {
    setIsSaving(true);
    isSavingRef.current = true;
    try {
      const currentOverrides = overridesRef.current;
      await (supabase as any)
        .from("global_design_defaults")
        .upsert({
          page_scope: "element-overrides",
          config: currentOverrides,
          updated_at: new Date().toISOString(),
        }, { onConflict: "page_scope" });
    } finally {
      setIsSaving(false);
      setTimeout(() => { isSavingRef.current = false; }, 3000);
    }
  }, []);

  return (
    <ElementOverrideContext.Provider value={{
      overrides,
      getElementStyle,
      setElementProp,
      resetElement,
      saveToCloud: saveToCloudFn,
      isSaving,
      isSelectMode,
      setSelectMode,
      selectedElement,
      setSelectedElement,
    }}>
      {children}
    </ElementOverrideContext.Provider>
  );
}
